import React, { useEffect, useState, useMemo } from 'react';
import {
  Shield,
  ShieldCheck,
  Users,
  UserCheck,
  CheckCircle2,
  XCircle,
  Save,
  RotateCcw,
  UserPlus,
  Trash2,
  Layers,
  Mail,
  Megaphone,
  FileText,
  Gauge,
  CreditCard,
  Key,
  Activity,
  ShieldAlert,
  Loader2,
  ChevronDown,
  ChevronRight,
  Search,
  Eye,
  Lock,
  Plus,
  Edit3,
  Filter,
  Check,
  X,
  Undo2,
  Sparkles,
} from 'lucide-react';
import { apiClient } from '../services/api-client';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import type { OrganizationMemberDto } from '@antigravity/types';

interface UamScreen {
  id: string;
  code: string;
  name: string;
  category: string;
  route: string;
  description: string;
  sortOrder: number;
  isActive: boolean;
  permissions: {
    id: string;
    screenId: string;
    actionKey: string;
    actionName: string;
    description: string;
    sortOrder: number;
    roles: Record<string, boolean>;
  }[];
}

interface RolePermRecord {
  id: string;
  role: string;
  screenId: string;
  permissionId: string;
  isGranted: boolean;
  screenName: string;
  category: string;
  route: string;
  actionName: string;
  actionKey: string;
}

const ROLES: Array<'SUPER_ADMIN' | 'OWNER'> = [
  'SUPER_ADMIN',
  'OWNER',
];


const ROLE_THEMES: Record<
  string,
  {
    badge: string;
    border: string;
    text: string;
    headerBg: string;
    glow: string;
    icon: React.ElementType;
    shortTitle: string;
  }
> = {
  SUPER_ADMIN: {
    badge: 'bg-purple-950/80 text-purple-300 border-purple-700/80 shadow-[0_0_10px_rgba(168,85,247,0.2)]',
    border: 'border-purple-500/30',
    text: 'text-purple-400',
    headerBg: 'bg-purple-950/30',
    glow: 'from-purple-600/10',
    icon: ShieldAlert,
    shortTitle: 'Root SuperAdmin',
  },
  OWNER: {
    badge: 'bg-amber-950/80 text-amber-300 border-amber-700/80 shadow-[0_0_10px_rgba(245,158,11,0.2)]',
    border: 'border-amber-500/30',
    text: 'text-amber-400',
    headerBg: 'bg-amber-950/30',
    glow: 'from-amber-600/10',
    icon: ShieldCheck,
    shortTitle: 'Tenant Owner',
  },
  ADMIN: {
    badge: 'bg-blue-950/80 text-blue-300 border-blue-700/80 shadow-[0_0_10px_rgba(59,130,246,0.2)]',
    border: 'border-blue-500/30',
    text: 'text-blue-400',
    headerBg: 'bg-blue-950/30',
    glow: 'from-blue-600/10',
    icon: Shield,
    shortTitle: 'Workspace Admin',
  },
  MEMBER: {
    badge: 'bg-emerald-950/80 text-emerald-300 border-emerald-700/80 shadow-[0_0_10px_rgba(16,185,129,0.2)]',
    border: 'border-emerald-500/30',
    text: 'text-emerald-400',
    headerBg: 'bg-emerald-950/30',
    glow: 'from-emerald-600/10',
    icon: Users,
    shortTitle: 'Standard Member',
  },
  VIEWER: {
    badge: 'bg-slate-900 text-slate-300 border-slate-700 shadow-[0_0_10px_rgba(148,163,184,0.1)]',
    border: 'border-slate-600/30',
    text: 'text-slate-400',
    headerBg: 'bg-slate-900/50',
    glow: 'from-slate-600/10',
    icon: Eye,
    shortTitle: 'Auditor Viewer',
  },
};

export const getRoleTheme = (roleCode: string) => {
  return (
    ROLE_THEMES[roleCode] || {
      badge: 'bg-indigo-950/80 text-indigo-300 border-indigo-700/80 shadow-[0_0_10px_rgba(99,102,241,0.2)]',
      border: 'border-indigo-500/30',
      text: 'text-indigo-400',
      headerBg: 'bg-indigo-950/30',
      glow: 'from-indigo-600/10',
      icon: Shield,
      shortTitle: roleCode,
    }
  );
};

const SCREEN_ICONS: Record<string, React.ElementType> = {
  overview: Layers,
  webmail: Mail,
  campaigns: Megaphone,
  templates: FileText,
  contacts: Users,
  deliverability: Gauge,
  team: UserCheck,
  billing: CreditCard,
  api_keys: Key,
  diagnostics: Activity,
  uam: ShieldCheck,
  platform_admin: ShieldAlert,
};

const ROLE_METADATA: Record<
  'SUPER_ADMIN' | 'OWNER',
  {
    title: string;
    description: string;
    level: string;
  }
> = {
  SUPER_ADMIN: {
    title: 'Platform Super Administrator',
    description: 'Platform superuser with sovereign oversight across all multi-tenant workspaces and administrative consoles.',
    level: 'Tier 0 — Root Sovereign',
  },
  OWNER: {
    title: 'Workspace Owner',
    description: 'Primary tenant authority with commercial control, billing, team roster administration, and campaign operations.',
    level: 'Tier 1 — Workspace Sovereign',
  },
};

export const UamPage: React.FC = () => {
  const { currentOrg } = useAuth();
  const { refreshPermissions } = usePermissions();
  const currentOrgId = currentOrg?.id || 'a0000000-0000-0000-0000-000000000001';
  const orgName = currentOrg?.name || 'keerav camp';

  // Active view tab: Matrix first for immediate access control configuration
  const [activeTab, setActiveTab] = useState<'matrix' | 'members' | 'roles'>('matrix');
  const [screens, setScreens] = useState<UamScreen[]>([]);
  const [initialSnapshot, setInitialSnapshot] = useState<Record<string, boolean>>({});
  const [rolePerms, setRolePerms] = useState<RolePermRecord[]>([]);

  const [members, setMembers] = useState<OrganizationMemberDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Category filter for matrix
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');

  // Expanded screen accordions in matrix view
  const [expandedScreens, setExpandedScreens] = useState<Record<string, boolean>>({
    overview: true,
    webmail: true,
    campaigns: true,
    templates: true,
    contacts: true,
    deliverability: true,
    team: true,
    billing: true,
    api_keys: true,
    diagnostics: true,
    uam: true,
    platform_admin: true,
  });

  // Custom Roles state
  const [customRoles, setCustomRoles] = useState<any[]>([]);
  const [showCreateRoleModal, setShowCreateRoleModal] = useState<boolean>(false);
  const [showEditRoleModal, setShowEditRoleModal] = useState<boolean>(false);
  const [editingRole, setEditingRole] = useState<any | null>(null);
  const [roleFormData, setRoleFormData] = useState({
    code: '',
    name: '',
    description: '',
    permissions: [] as string[],
  });
  const [roleActionLoading, setRoleActionLoading] = useState<boolean>(false);

  // Create Employee with Screen Permissions Modal
  const [showCreateEmployeeModal, setShowCreateEmployeeModal] = useState<boolean>(false);
  const [creatingEmployee, setCreatingEmployee] = useState<boolean>(false);
  const [employeeFormData, setEmployeeFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'MEMBER',
  });
  const [employeeScreenToggles, setEmployeeScreenToggles] = useState<Record<string, boolean>>({
    '/': true,
    '/webmail': true,
    '/campaigns': true,
    '/templates': true,
    '/contacts': true,
    '/deliverability': false,
    '/team': false,
    '/billing': false,
    '/api-keys': false,
    '/uam': false,
    '/health': false,
    '/admin': false,
  });

  // Edit Existing Member Screen Permissions Modal
  const [editingMember, setEditingMember] = useState<OrganizationMemberDto | null>(null);
  const [memberScreenToggles, setMemberScreenToggles] = useState<Record<string, boolean>>({});
  const [loadingMemberPerms, setLoadingMemberPerms] = useState<boolean>(false);
  const [savingMemberPerms, setSavingMemberPerms] = useState<boolean>(false);

  // Invite member modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<string>('MEMBER');
  const [inviting, setInviting] = useState(false);

  // Search filter
  const [searchQuery, setSearchQuery] = useState('');

  // Helper to create snapshot for dirty diff checking
  const buildSnapshot = (screensList: UamScreen[]) => {
    const snap: Record<string, boolean> = {};
    for (const s of screensList) {
      for (const p of s.permissions) {
        for (const role of ROLES) {
          snap[`${role}___${p.id}`] = Boolean(p.roles[role]);
        }
      }
    }
    return snap;
  };

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [screensData, rolePermsData, membersData, customRolesData] = await Promise.all([
        apiClient.getUamScreens(),
        apiClient.getUamRolePermissions(),
        apiClient.getUamOrganizationMembers(currentOrgId).catch(() => []),
        apiClient.getCustomRoles(currentOrgId).catch(() => []),
      ]);

      setScreens(screensData);
      setInitialSnapshot(buildSnapshot(screensData));
      setRolePerms(rolePermsData);
      setMembers(membersData);
      setCustomRoles(customRolesData);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to load UAM data from database' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllData();
  }, [currentOrgId]);

  // Compute number of modified permissions
  const modifiedCount = useMemo(() => {
    if (!screens.length || !Object.keys(initialSnapshot).length) return 0;
    let count = 0;
    for (const s of screens) {
      for (const p of s.permissions) {
        for (const role of ROLES) {
          const key = `${role}___${p.id}`;
          if (Boolean(p.roles[role]) !== Boolean(initialSnapshot[key])) {
            count++;
          }
        }
      }
    }
    return count;
  }, [screens, initialSnapshot]);

  // Reset matrix back to last loaded snapshot
  const handleDiscardChanges = () => {
    setScreens((prev) =>
      prev.map((s) => ({
        ...s,
        permissions: s.permissions.map((p) => {
          const restoredRoles: any = {};
          for (const role of ROLES) {
            const key = `${role}___${p.id}`;
            restoredRoles[role] = initialSnapshot[key] !== undefined ? initialSnapshot[key] : p.roles[role];
          }
          return {
            ...p,
            roles: restoredRoles,
          };
        }),
      }))
    );
    setMessage({ type: 'success', text: 'All unsaved matrix modifications were reset.' });
    setTimeout(() => setMessage(null), 3000);
  };

  // Toggle single action permission
  const handleTogglePermission = (screenId: string, permId: string, role: string, actionKey: string) => {
    // Safety guard: Never allow disabling core UAM viewing for SuperAdmin
    if (actionKey === 'uam:view_members' && role === 'SUPER_ADMIN') {
      setMessage({
        type: 'error',
        text: 'Access Control (UAM) view cannot be disabled for SuperAdmin to prevent system lockout.',
      });
      setTimeout(() => setMessage(null), 4000);
      return;
    }

    setScreens((prev) =>
      prev.map((s) => {
        if (s.id !== screenId) return s;
        return {
          ...s,
          permissions: s.permissions.map((p) => {
            if (p.id !== permId) return p;
            return {
              ...p,
              roles: {
                ...p.roles,
                [role as any]: !p.roles[role as keyof typeof p.roles],
              },
            };
          }),
        };
      })
    );
  };

  // Toggle ALL actions of a screen for a role at once (1-Click screen grant/revoke)
  const handleToggleScreenForRole = (screenId: string, role: string) => {
    const screen = screens.find((s) => s.id === screenId);
    if (!screen) return;

    // Safety guard: Never turn off UAM for SuperAdmin
    if (screen.code === 'uam' && role === 'SUPER_ADMIN') {
      setMessage({
        type: 'error',
        text: 'Access Control (UAM) cannot be revoked for SuperAdmin.',
      });
      setTimeout(() => setMessage(null), 3500);
      return;
    }

    // Determine target state: if any action is granted, revoke all; if all are off, grant all
    const anyGranted = screen.permissions.some((p) => Boolean(p.roles[role as keyof typeof p.roles]));
    const nextState = !anyGranted;

    setScreens((prev) =>
      prev.map((s) => {
        if (s.id !== screenId) return s;
        return {
          ...s,
          permissions: s.permissions.map((p) => ({
            ...p,
            roles: {
              ...p.roles,
              [role as any]: nextState,
            },
          })),
        };
      })
    );
  };

  // Save full matrix back to database and immediately refresh session permissions
  const handleSaveMatrix = async () => {
    try {
      setSaving(true);
      const updates: Array<{ role: string; permissionId: string; isGranted: boolean }> = [];

      for (const s of screens) {
        for (const p of s.permissions) {
          for (const role of ROLES) {
            updates.push({
              role,
              permissionId: p.id,
              isGranted: Boolean(p.roles[role]),
            });
          }
        }
      }

      await apiClient.updateUamRolePermissionsBatch(updates);
      setInitialSnapshot(buildSnapshot(screens));

      // Refresh active user's permissions context so sidebar and route guards react instantly!
      await refreshPermissions();

      setMessage({
        type: 'success',
        text: `Successfully persisted ${updates.length} screen permissions to database! Sidebar and route access updated.`,
      });
      setTimeout(() => setMessage(null), 5000);
      await loadAllData();
    } catch (err: any) {
      setMessage({ type: 'error', text: `Failed to save matrix: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  // Role change handler for team member
  const handleRoleChange = async (memberId: string, newRole: string) => {
    try {
      await apiClient.updateMemberRole(currentOrgId, memberId, { role: newRole as any });
      setMessage({ type: 'success', text: `Role updated to ${newRole} successfully!` });
      setTimeout(() => setMessage(null), 3000);
      await loadAllData();
    } catch (err: any) {
      setMessage({ type: 'error', text: `Failed to update member role: ${err.message}` });
    }
  };

  // Invite member
  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail) return;

    try {
      setInviting(true);
      await apiClient.inviteMember(currentOrgId, { email: inviteEmail, role: inviteRole as any });
      setMessage({ type: 'success', text: `Invitation sent to ${inviteEmail} with role ${inviteRole}!` });
      setShowInviteModal(false);
      setInviteEmail('');
      setTimeout(() => setMessage(null), 4000);
      await loadAllData();
    } catch (err: any) {
      setMessage({ type: 'error', text: `Failed to invite member: ${err.message}` });
    } finally {
      setInviting(false);
    }
  };

  // Remove member
  const handleRemoveMember = async (memberId: string, memberName: string) => {
    if (!window.confirm(`Are you sure you want to remove ${memberName} from this workspace?`)) return;

    try {
      await apiClient.removeMember(currentOrgId, memberId);
      setMessage({ type: 'success', text: `Removed ${memberName} from workspace.` });
      setTimeout(() => setMessage(null), 3000);
      await loadAllData();
    } catch (err: any) {
      setMessage({ type: 'error', text: `Failed to remove member: ${err.message}` });
    }
  };

  // Create Employee with Screen Visibility Permissions
  const handleCreateEmployee = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreatingEmployee(true);
    setMessage(null);
    try {
      const screenPermissions = Object.entries(employeeScreenToggles).map(([route, isAllowed]) => ({
        route,
        isAllowed,
      }));
      await apiClient.createUamEmployee(currentOrgId, {
        firstName: employeeFormData.firstName,
        lastName: employeeFormData.lastName,
        email: employeeFormData.email,
        password: employeeFormData.password,
        role: employeeFormData.role as any,
        screenPermissions,
      });
      setMessage({ type: 'success', text: `Employee ${employeeFormData.firstName} created and mapped to workspace!` });
      setShowCreateEmployeeModal(false);
      setEmployeeFormData({ firstName: '', lastName: '', email: '', password: '', role: 'MEMBER' });
      await loadAllData();
      await refreshPermissions();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to create employee' });
    } finally {
      setCreatingEmployee(false);
    }
  };

  // Open Edit Screen Permissions Modal
  const handleOpenEditPermissions = async (member: OrganizationMemberDto) => {
    setEditingMember(member);
    setLoadingMemberPerms(true);
    try {
      const perms = await apiClient.getMemberScreenPermissions(currentOrgId, member.id);
      const toggleMap: Record<string, boolean> = {};
      screens.forEach((s) => {
        const found = perms.find((p) => p.route === s.route);
        toggleMap[s.route] = found ? Boolean(found.isAllowed) : false;
      });
      setMemberScreenToggles(toggleMap);
    } catch (err: any) {
      setMessage({ type: 'error', text: 'Failed to load member screen permissions' });
    } finally {
      setLoadingMemberPerms(false);
    }
  };

  // Save Member Screen Permissions
  const handleSaveMemberPermissions = async () => {
    if (!editingMember) return;
    setSavingMemberPerms(true);
    try {
      const permissions = Object.entries(memberScreenToggles).map(([route, isAllowed]) => ({
        route,
        isAllowed,
      }));
      await apiClient.updateMemberScreenPermissions(currentOrgId, editingMember.id, permissions);
      setMessage({ type: 'success', text: `Screen permissions updated for ${editingMember.user?.firstName || 'Employee'}` });
      setEditingMember(null);
      await refreshPermissions();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to save screen permissions' });
    } finally {
      setSavingMemberPerms(false);
    }
  };

  // Custom Roles CRUD
  const handleCreateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    setRoleActionLoading(true);
    setMessage(null);
    try {
      await apiClient.createCustomRole({
        organizationId: currentOrgId,
        code: roleFormData.code,
        name: roleFormData.name,
        description: roleFormData.description,
        permissions: roleFormData.permissions,
      });
      setMessage({ type: 'success', text: `Role ${roleFormData.name} created successfully!` });
      setShowCreateRoleModal(false);
      setRoleFormData({ code: '', name: '', description: '', permissions: [] });
      await loadAllData();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to create role' });
    } finally {
      setRoleActionLoading(false);
    }
  };

  const handleUpdateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingRole) return;
    setRoleActionLoading(true);
    try {
      await apiClient.updateCustomRole(editingRole.id, currentOrgId, {
        name: roleFormData.name,
        description: roleFormData.description,
        permissions: roleFormData.permissions,
      });
      setMessage({ type: 'success', text: `Role ${roleFormData.name} updated successfully!` });
      setShowEditRoleModal(false);
      setEditingRole(null);
      await loadAllData();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to update role' });
    } finally {
      setRoleActionLoading(false);
    }
  };

  const handleDeleteRole = async (roleId: string, roleName: string, roleCode?: string) => {
    if (!window.confirm(`Are you sure you want to delete role "${roleName}" (${roleCode || roleName})? Any members currently assigned to this role will be reassigned safely.`)) return;
    try {
      await apiClient.deleteCustomRole(roleId, currentOrgId);
      setMessage({ type: 'success', text: `Role "${roleName}" deleted successfully` });
      setTimeout(() => setMessage(null), 3000);
      await loadAllData();
      await refreshPermissions();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to delete role' });
    }
  };

  const toggleAccordion = (code: string) => {
    setExpandedScreens((prev) => ({ ...prev, [code]: !prev[code] }));
  };

  // All distinct categories
  const categories = useMemo(() => {
    const set = new Set<string>();
    screens.forEach((s) => set.add(s.category));
    return ['ALL', ...Array.from(set)];
  }, [screens]);

  // Filtered screens
  const filteredScreens = screens.filter((s) => {
    const matchesCategory = selectedCategory === 'ALL' || s.category === selectedCategory;
    const matchesQuery =
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.route.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.permissions.some((p) => p.actionName.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesQuery;
  });

  return (
    <div className="space-y-6 pb-24">
      {/* Top Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-4 border-b border-slate-800/80">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-tr from-brand-600/20 to-indigo-600/20 border border-brand-500/30 flex items-center justify-center text-brand-400 shadow-[0_0_20px_rgba(59,130,246,0.15)]">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight text-white flex items-center gap-2.5">
                <span>Access Control & Permissions (UAM)</span>
                <span className="text-xs px-2.5 py-0.5 rounded-full bg-slate-900 text-brand-300 font-mono border border-slate-700/80">
                  {orgName}
                </span>
              </h1>
              <p className="text-xs text-slate-400 mt-0.5">
                Configure fine-grained screen visibility and action capabilities across default platform roles (SUPER_ADMIN and OWNER). Changes sync directly to the live navigation engine.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={loadAllData}
            disabled={loading}
            className="px-3.5 py-2 rounded-xl text-xs font-semibold bg-slate-900/90 border border-slate-800 text-slate-300 hover:text-white hover:bg-slate-800/80 transition flex items-center gap-2 shadow-sm"
          >
            <RotateCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>

          {activeTab === 'matrix' && (
            <button
              onClick={handleSaveMatrix}
              disabled={saving || modifiedCount === 0}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 shadow-lg ${
                modifiedCount > 0
                  ? 'bg-gradient-to-r from-brand-600 to-indigo-600 hover:from-brand-500 hover:to-indigo-500 text-white shadow-brand-500/30 cursor-pointer animate-pulse'
                  : 'bg-slate-900 border border-slate-800 text-slate-500 cursor-not-allowed'
              }`}
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              <span>Save Matrix</span>
              {modifiedCount > 0 && (
                <span className="ml-1 px-1.5 py-0.2 rounded-full bg-white/20 text-white text-[10px] font-mono">
                  {modifiedCount}
                </span>
              )}
            </button>
          )}

          {activeTab === 'members' && (
            <button
              onClick={() => setShowCreateEmployeeModal(true)}
              className="px-4 py-2 rounded-xl text-xs font-bold bg-gradient-to-r from-brand-600 to-indigo-600 hover:from-brand-500 hover:to-indigo-500 text-white transition flex items-center gap-2 shadow-lg shadow-brand-500/20"
            >
              <UserPlus className="w-3.5 h-3.5" />
              Create Employee
            </button>
          )}
        </div>
      </div>

      {/* Notification Toast */}
      {message && (
        <div
          className={`p-3.5 rounded-2xl border text-xs font-medium flex items-center justify-between shadow-xl transition-all ${
            message.type === 'success'
              ? 'bg-emerald-950/70 border-emerald-800/80 text-emerald-300'
              : 'bg-rose-950/70 border-rose-800/80 text-rose-300'
          }`}
        >
          <div className="flex items-center gap-2.5">
            {message.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
            ) : (
              <XCircle className="w-4 h-4 text-rose-400 shrink-0" />
            )}
            <span>{message.text}</span>
          </div>
          <button onClick={() => setMessage(null)} className="text-xs opacity-70 hover:opacity-100 px-2 py-0.5">
            Dismiss
          </button>
        </div>
      )}

      {/* KPI Stats Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3.5">
        <div className="bg-gradient-to-b from-slate-900/90 to-slate-950/90 border border-slate-800/80 rounded-2xl p-4 shadow-lg hover:border-slate-700/80 transition-all">
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-400">
            <Layers className="w-4 h-4 text-blue-400" />
            <span>Platform Screens</span>
          </div>
          <div className="text-2xl font-black text-white mt-1.5">{screens.length || 12}</div>
          <p className="text-[11px] text-slate-500 mt-0.5 font-mono">100% relational coverage</p>
        </div>

        <div className="bg-gradient-to-b from-slate-900/90 to-slate-950/90 border border-slate-800/80 rounded-2xl p-4 shadow-lg hover:border-slate-700/80 transition-all">
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-400">
            <Shield className="w-4 h-4 text-purple-400" />
            <span>Action Capabilities</span>
          </div>
          <div className="text-2xl font-black text-white mt-1.5">
            {screens.reduce((acc, s) => acc + s.permissions.length, 0) || 32}
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5 font-mono">Granular action toggles</p>
        </div>

        <div className="bg-gradient-to-b from-slate-900/90 to-slate-950/90 border border-slate-800/80 rounded-2xl p-4 shadow-lg hover:border-slate-700/80 transition-all">
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-400">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <span>Role Permissions</span>
          </div>
          <div className="text-2xl font-black text-white mt-1.5">{rolePerms.length || 160}</div>
          <p className="text-[11px] text-slate-500 mt-0.5 font-mono">Across 2 default platform roles</p>
        </div>

        <div className="bg-gradient-to-b from-slate-900/90 to-slate-950/90 border border-slate-800/80 rounded-2xl p-4 shadow-lg hover:border-slate-700/80 transition-all">
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-400">
            <Users className="w-4 h-4 text-amber-400" />
            <span>Active Team Members</span>
          </div>
          <div className="text-2xl font-black text-white mt-1.5">{members.length}</div>
          <p className="text-[11px] text-slate-500 mt-0.5 font-mono">{orgName} Workspace</p>
        </div>
      </div>

      {/* Main Navigation Tabs */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800/80 pb-2">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('matrix')}
            className={`px-4 py-2.5 rounded-xl text-xs font-bold transition flex items-center gap-2 ${
              activeTab === 'matrix'
                ? 'bg-gradient-to-r from-brand-600/25 to-indigo-600/25 text-brand-300 border border-brand-500/40 shadow-sm'
                : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
            }`}
          >
            <ShieldCheck className="w-4 h-4 text-brand-400" />
            <span>Full Screen Permission Matrix (All 12 Screens)</span>
          </button>

          <button
            onClick={() => setActiveTab('members')}
            className={`px-4 py-2.5 rounded-xl text-xs font-bold transition flex items-center gap-2 ${
              activeTab === 'members'
                ? 'bg-gradient-to-r from-brand-600/25 to-indigo-600/25 text-brand-300 border border-brand-500/40 shadow-sm'
                : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
            }`}
          >
            <Users className="w-4 h-4 text-amber-400" />
            <span>Team Members & Screen Visibility ({members.length})</span>
          </button>

          <button
            onClick={() => setActiveTab('roles')}
            className={`px-4 py-2.5 rounded-xl text-xs font-bold transition flex items-center gap-2 ${
              activeTab === 'roles'
                ? 'bg-gradient-to-r from-brand-600/25 to-indigo-600/25 text-brand-300 border border-brand-500/40 shadow-sm'
                : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
            }`}
          >
            <Shield className="w-4 h-4 text-purple-400" />
            <span>Workspace Custom Roles ({customRoles.filter((r) => !r.isSystem).length})</span>
          </button>
        </div>

        {/* Search */}
        <div className="relative w-64">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            placeholder="Search screens, actions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-7 py-1.5 rounded-xl bg-slate-900 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-brand-500 transition"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="py-24 flex flex-col items-center justify-center text-slate-400 gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-brand-500" />
          <p className="text-sm font-semibold text-white">Loading UAM Access Matrix from Database...</p>
          <p className="text-xs text-slate-500">Evaluating roles, screen permissions, and membership topology</p>
        </div>
      ) : (
        <>
          {/* ========================================================================= */}
          {/* TAB 1: FULL SCREEN PERMISSION MATRIX (MASTER GRID) */}
          {/* ========================================================================= */}
          {activeTab === 'matrix' && (
            <div className="space-y-4">
              {/* Category Filter Pills & Quick Controls */}
              <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-slate-900/60 border border-slate-800/80 rounded-2xl backdrop-blur-md">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mr-1 flex items-center gap-1">
                    <Filter className="w-3 h-3 text-brand-400" /> Filter:
                  </span>
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition ${
                        selectedCategory === cat
                          ? 'bg-brand-600 text-white shadow-sm shadow-brand-500/20'
                          : 'bg-slate-950/60 text-slate-400 hover:text-slate-200 hover:bg-slate-800'
                      }`}
                    >
                      {cat === 'ALL' ? `All Screens (${screens.length})` : cat}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-2 text-xs">
                  <button
                    onClick={() => {
                      const all: Record<string, boolean> = {};
                      screens.forEach((s) => (all[s.code] = true));
                      setExpandedScreens(all);
                    }}
                    className="text-slate-400 hover:text-white text-[11px] font-medium px-2 py-1 rounded hover:bg-slate-800/60"
                  >
                    Expand All
                  </button>
                  <span className="text-slate-700">|</span>
                  <button
                    onClick={() => {
                      const none: Record<string, boolean> = {};
                      screens.forEach((s) => (none[s.code] = false));
                      setExpandedScreens(none);
                    }}
                    className="text-slate-400 hover:text-white text-[11px] font-medium px-2 py-1 rounded hover:bg-slate-800/60"
                  >
                    Collapse All
                  </button>
                </div>
              </div>

              {/* Master Permission Matrix Table */}
              <div className="bg-slate-900/40 border border-slate-800/90 rounded-2xl overflow-hidden shadow-2xl backdrop-blur-xl">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-slate-800 bg-slate-950/80 text-[11px] uppercase tracking-wider text-slate-400 font-bold">
                        <th className="py-4 px-5 min-w-[340px]">
                          <div className="flex items-center gap-2">
                            <span>Screen / Action Capability</span>
                            <span className="text-[10px] text-slate-500 font-normal">
                              ({filteredScreens.length} of {screens.length} screens shown)
                            </span>
                          </div>
                        </th>

                        {ROLES.map((role) => {
                          const theme = ROLE_THEMES[role];
                          const Icon = theme.icon;
                          const totalRolePerms = rolePerms.filter((rp) => rp.role === role).length || 32;
                          const grantedCount = screens.reduce((acc, s) => {
                            return acc + s.permissions.filter((p) => Boolean(p.roles[role])).length;
                          }, 0);

                          return (
                            <th key={role} className={`py-3.5 px-3 text-center w-32 ${theme.headerBg} border-l border-slate-800/60`}>
                              <div className="flex flex-col items-center gap-1">
                                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-[10px] font-bold border ${theme.badge}`}>
                                  <Icon className="w-3 h-3" />
                                  <span>{role}</span>
                                </span>
                                <span className="text-[10px] font-mono text-slate-400 font-medium">
                                  {grantedCount}/{totalRolePerms} actions
                                </span>
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-800/70 text-xs">
                      {filteredScreens.map((screen) => {
                        const IconComponent = SCREEN_ICONS[screen.code] || Layers;
                        const isExpanded = expandedScreens[screen.code];

                        return (
                          <React.Fragment key={screen.id}>
                            {/* Screen Master Header Row */}
                            <tr className="bg-slate-900/80 hover:bg-slate-800/50 transition-colors">
                              <td className="py-3 px-5">
                                <div
                                  onClick={() => toggleAccordion(screen.code)}
                                  className="flex items-center justify-between cursor-pointer select-none group"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-xl bg-slate-800/90 border border-slate-700 flex items-center justify-center text-slate-300 group-hover:text-brand-300 group-hover:border-brand-500/40 transition">
                                      <IconComponent className="w-4 h-4" />
                                    </div>
                                    <div>
                                      <div className="font-bold text-white flex items-center gap-2">
                                        <span>{screen.name}</span>
                                        <span className="text-[10px] text-slate-300 font-mono bg-slate-800/90 px-2 py-0.5 rounded-md border border-slate-700/80">
                                          {screen.route}
                                        </span>
                                        <span className="text-[10px] text-brand-300 font-semibold bg-brand-950/60 px-2 py-0.5 rounded-md border border-brand-800/50">
                                          {screen.category}
                                        </span>
                                      </div>
                                      <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-1">{screen.description}</p>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-2 text-slate-400 group-hover:text-white transition">
                                    <span className="text-[11px] text-slate-400 font-medium">
                                      {screen.permissions.length} actions
                                    </span>
                                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                  </div>
                                </div>
                              </td>

                              {/* Screen-level 1-click toggles for each role */}
                              {ROLES.map((role) => {
                                const totalActions = screen.permissions.length;
                                const grantedActions = screen.permissions.filter((p) => Boolean(p.roles[role])).length;
                                const isAllGranted = grantedActions === totalActions && totalActions > 0;
                                const isPartial = grantedActions > 0 && grantedActions < totalActions;
                                const isLockedUam = screen.code === 'uam' && role === 'SUPER_ADMIN';

                                return (
                                  <td
                                    key={role}
                                    className="py-3 px-3 text-center border-l border-slate-800/60 bg-slate-950/30"
                                  >
                                    {isLockedUam ? (
                                      <div
                                        title="Core access control screen is protected to prevent administrative lockout."
                                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold bg-slate-900 border border-slate-800 text-slate-400 cursor-not-allowed select-none"
                                      >
                                        <Lock className="w-3 h-3 text-amber-500/80" />
                                        <span>LOCKED</span>
                                      </div>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => handleToggleScreenForRole(screen.id, role)}
                                        title={`Click to ${isAllGranted ? 'revoke all' : 'grant all'} actions in ${screen.name} for ${role}`}
                                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold transition select-none cursor-pointer ${
                                          isAllGranted
                                            ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30 shadow-[0_0_10px_rgba(16,185,129,0.15)]'
                                            : isPartial
                                            ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40 hover:bg-amber-500/30'
                                            : 'bg-slate-900 text-slate-500 border border-slate-800 hover:text-slate-300 hover:border-slate-700'
                                        }`}
                                      >
                                        {isAllGranted ? (
                                          <>
                                            <Check className="w-3 h-3 text-emerald-400" />
                                            <span>ALL</span>
                                          </>
                                        ) : isPartial ? (
                                          <>
                                            <Sparkles className="w-3 h-3 text-amber-400" />
                                            <span>{grantedActions}/{totalActions}</span>
                                          </>
                                        ) : (
                                          <>
                                            <X className="w-3 h-3 text-slate-500" />
                                            <span>OFF</span>
                                          </>
                                        )}
                                      </button>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>

                            {/* Sub-Action Capability Rows */}
                            {isExpanded &&
                              screen.permissions.map((perm) => (
                                <tr
                                  key={perm.id}
                                  className="hover:bg-slate-800/30 transition-colors bg-slate-950/40 border-t border-slate-900"
                                >
                                  <td className="py-2.5 pl-14 pr-5">
                                    <div className="flex items-start gap-2">
                                      <div className="w-1.5 h-1.5 rounded-full bg-slate-600 mt-1.5" />
                                      <div>
                                        <div className="font-semibold text-slate-200 text-xs">{perm.actionName}</div>
                                        <div className="text-[11px] text-slate-400 mt-0.5">{perm.description}</div>
                                        <div className="text-[10px] font-mono text-slate-500 mt-0.5">{perm.actionKey}</div>
                                      </div>
                                    </div>
                                  </td>

                                  {ROLES.map((role) => {
                                    const isGranted = Boolean(perm.roles[role]);
                                    const isLocked = perm.actionKey === 'uam:view_members' && role === 'SUPER_ADMIN';

                                    return (
                                      <td
                                        key={role}
                                        className="py-2.5 px-3 text-center border-l border-slate-800/60"
                                      >
                                        {isLocked ? (
                                          <div
                                            title="SuperAdmin and Owner cannot disable UAM member view."
                                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold bg-slate-900 border border-slate-800 text-slate-400 cursor-not-allowed select-none"
                                          >
                                            <Lock className="w-3 h-3 text-amber-500/80" />
                                            <span>CORE</span>
                                          </div>
                                        ) : (
                                          <button
                                            type="button"
                                            onClick={() => handleTogglePermission(screen.id, perm.id, role, perm.actionKey)}
                                            className={`h-7 px-3 rounded-lg text-[11px] font-bold inline-flex items-center gap-1.5 transition cursor-pointer select-none ${
                                              isGranted
                                                ? 'bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 shadow-[0_0_12px_rgba(16,185,129,0.15)]'
                                                : 'bg-slate-900/90 border border-slate-800 text-slate-500 hover:text-rose-400 hover:border-rose-900/50 hover:bg-rose-950/20'
                                            }`}
                                            title={`${role}: ${isGranted ? 'Allowed' : 'Denied'}`}
                                          >
                                            {isGranted ? (
                                              <>
                                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                                                <span>ALLOW</span>
                                              </>
                                            ) : (
                                              <>
                                                <XCircle className="w-3.5 h-3.5 text-slate-500" />
                                                <span>DENIED</span>
                                              </>
                                            )}
                                          </button>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Floating Unsaved Changes Bottom Banner */}
              {modifiedCount > 0 && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-full max-w-xl px-4 animate-in fade-in slide-in-from-bottom-4 duration-300">
                  <div className="bg-slate-900/95 backdrop-blur-xl border border-brand-500/40 shadow-[0_10px_40px_rgba(0,0,0,0.8),0_0_25px_rgba(59,130,246,0.25)] rounded-2xl p-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-brand-500/20 border border-brand-500/40 flex items-center justify-center text-brand-400 shrink-0">
                        <Sparkles className="w-5 h-5 animate-pulse" />
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-white flex items-center gap-1.5">
                          <span>{modifiedCount} Permissions Modified</span>
                          <span className="text-[10px] px-2 py-0.2 rounded-full bg-brand-950 text-brand-300 border border-brand-700 font-mono">
                            Unsaved
                          </span>
                        </h4>
                        <p className="text-[11px] text-slate-400">
                          Changes will take effect across the sidebar and route guards immediately upon saving.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={handleDiscardChanges}
                        disabled={saving}
                        className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white transition flex items-center gap-1.5"
                      >
                        <Undo2 className="w-3.5 h-3.5" />
                        <span>Discard</span>
                      </button>

                      <button
                        onClick={handleSaveMatrix}
                        disabled={saving}
                        className="px-4 py-1.5 rounded-xl text-xs font-bold bg-gradient-to-r from-brand-600 to-indigo-600 hover:from-brand-500 hover:to-indigo-500 text-white shadow-lg shadow-brand-500/30 transition flex items-center gap-1.5"
                      >
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        <span>Save Changes</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ========================================================================= */}
          {/* TAB 2: TEAM MEMBERS & ROLE GOVERNANCE */}
          {/* ========================================================================= */}
          {activeTab === 'members' && (
            <div className="space-y-4">
              <div className="bg-slate-900/40 border border-slate-800/90 rounded-2xl overflow-hidden shadow-2xl backdrop-blur-xl">
                <div className="p-4 bg-slate-900/80 border-b border-slate-800 flex items-center justify-between">
                  <div>
                    <h2 className="text-sm font-bold text-white flex items-center gap-2">
                      <Users className="w-4 h-4 text-amber-400" />
                      <span>Team Member Access Roster</span>
                      <span className="text-xs px-2.5 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700 font-mono">
                        {members.length} Members
                      </span>
                    </h2>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Assign roles (`OWNER`, `ADMIN`, `MEMBER`, `VIEWER`) or configure custom 12-screen visibility for individual employees.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowCreateEmployeeModal(true)}
                      className="px-3.5 py-1.5 rounded-xl text-xs font-bold bg-gradient-to-r from-brand-600 to-indigo-600 hover:from-brand-500 hover:to-indigo-500 text-white transition flex items-center gap-1.5 shadow-md shadow-brand-950/40"
                    >
                      <UserPlus className="w-3.5 h-3.5" />
                      Create Employee & Permissions
                    </button>
                    <button
                      onClick={() => setShowInviteModal(true)}
                      className="px-3.5 py-1.5 rounded-xl text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-slate-300 transition flex items-center gap-1.5"
                    >
                      <Mail className="w-3.5 h-3.5" />
                      Invite User
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="border-b border-slate-800 bg-slate-900/60 text-[11px] uppercase tracking-wider text-slate-400 font-bold">
                        <th className="py-3 px-4">Member Name</th>
                        <th className="py-3 px-4">Email Address</th>
                        <th className="py-3 px-4">Current Role</th>
                        <th className="py-3 px-4">Status</th>
                        <th className="py-3 px-4 text-center">Screen Permissions</th>
                        <th className="py-3 px-4">Joined Date</th>
                        <th className="py-3 px-4 text-right">Role Assignment</th>
                        <th className="py-3 px-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/60">
                      {members.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="py-12 text-center text-slate-400 space-y-3">
                            <div className="w-12 h-12 rounded-2xl bg-brand-500/10 border border-brand-500/20 mx-auto flex items-center justify-center text-brand-400">
                              <Users className="w-6 h-6" />
                            </div>
                            <div className="space-y-1">
                              <p className="text-sm font-semibold text-white">No Team Members Provisioned Yet</p>
                              <p className="text-xs text-slate-400">Add employees and configure their specific screen permissions to start collaborating.</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setShowCreateEmployeeModal(true)}
                              className="px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 text-white text-xs font-semibold shadow-md inline-flex items-center gap-1.5 transition cursor-pointer"
                            >
                              <UserPlus className="w-4 h-4" />
                              <span>Create First Employee</span>
                            </button>
                          </td>
                        </tr>
                      ) : (
                        members.map((member) => {
                          const fullName = `${member.user?.firstName || 'User'} ${member.user?.lastName || ''}`.trim();
                          const isOwner = member.role === 'OWNER';

                          return (
                            <tr key={member.id} className="hover:bg-slate-800/30 transition-colors">
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center font-bold text-xs text-brand-300">
                                    {member.user?.firstName?.[0] || 'U'}
                                    {member.user?.lastName?.[0] || ''}
                                  </div>
                                  <div className="font-semibold text-white">{fullName}</div>
                                </div>
                              </td>
                              <td className="py-3 px-4 font-mono text-slate-300">{member.user?.email}</td>
                              <td className="py-3 px-4">
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-[11px] font-semibold border ${
                                    ROLE_THEMES[member.role]?.badge || 'bg-slate-800 text-slate-300'
                                  }`}
                                >
                                  {member.role}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <span
                                  className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                    member.status === 'ACTIVE'
                                      ? 'bg-emerald-950/80 text-emerald-400 border border-emerald-800/80'
                                      : 'bg-amber-950/80 text-amber-400 border border-amber-800/80'
                                  }`}
                                >
                                  <span className="w-1.5 h-1.5 rounded-full bg-current" />
                                  {member.status}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-center">
                                {isOwner ? (
                                  <span className="text-[11px] text-amber-400 font-medium">All Screens Granted (Owner)</span>
                                ) : (
                                  <button
                                    onClick={() => handleOpenEditPermissions(member)}
                                    className="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-slate-800 hover:bg-slate-700 border border-slate-700 text-brand-300 hover:text-white transition inline-flex items-center gap-1.5 cursor-pointer"
                                  >
                                    <Eye className="w-3.5 h-3.5 text-brand-400" />
                                    Configure Screens
                                  </button>
                                )}
                              </td>
                              <td className="py-3 px-4 text-slate-400 font-mono">
                                {new Date(member.createdAt).toLocaleDateString()}
                              </td>
                              <td className="py-3 px-4 text-right">
                                <select
                                  value={member.role}
                                  onChange={(e) => handleRoleChange(member.id, e.target.value as any)}
                                  className="bg-slate-900 border border-slate-700 text-slate-200 text-xs rounded-lg px-2.5 py-1 focus:outline-none focus:border-brand-500 font-medium cursor-pointer"
                                >
                                  <option value="MEMBER">MEMBER</option>
                                  {customRoles
                                    .filter((cr) => !cr.isSystem && cr.code !== 'OWNER' && cr.code !== 'SUPER_ADMIN')
                                    .map((cr) => (
                                      <option key={cr.code} value={cr.code}>
                                        {cr.name} ({cr.code})
                                      </option>
                                    ))}
                                  {member.role !== 'OWNER' && member.role !== 'MEMBER' && !customRoles.some((cr) => cr.code === member.role) && (
                                    <option value={member.role}>{member.role}</option>
                                  )}
                                </select>
                              </td>
                              <td className="py-3 px-4 text-right">
                                {!isOwner ? (
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveMember(member.id, fullName)}
                                    className="p-1.5 text-slate-400 hover:text-rose-400 hover:bg-rose-950/40 rounded-lg transition"
                                    title="Remove Member"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                ) : (
                                  <span className="text-[11px] text-slate-600 italic">Workspace Owner</span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ========================================================================= */}
          {/* TAB 3: WORKSPACE CUSTOM ROLES */}
          {/* ========================================================================= */}
          {activeTab === 'roles' && (
            <div className="space-y-6">
              {/* Custom Roles Management Banner */}
              <div className="bg-slate-900/50 border border-slate-800/90 rounded-2xl p-5 shadow-xl backdrop-blur-xl">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-800">
                  <div>
                    <h3 className="text-sm font-bold text-white flex items-center gap-2">
                      <Shield className="w-4 h-4 text-brand-400" />
                      <span>Workspace Custom Roles</span>
                      <span className="text-xs px-2.5 py-0.5 rounded-full bg-slate-800 text-slate-300 font-mono border border-slate-700">
                        {customRoles.filter((r) => !r.isSystem).length} Custom
                      </span>
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Create, edit, or delete custom operational roles with specific screen and action permissions.
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setRoleFormData({ code: '', name: '', description: '', permissions: [] });
                      setShowCreateRoleModal(true);
                    }}
                    className="px-3.5 py-1.5 rounded-xl text-xs font-bold bg-brand-600 hover:bg-brand-500 text-white transition flex items-center gap-1.5 shadow-md shadow-brand-950/40 w-fit"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Create Custom Role
                  </button>
                </div>

                {customRoles.filter((r) => !r.isSystem).length === 0 ? (
                  <div className="p-6 text-center text-slate-500 text-xs">
                    No custom roles defined yet. Default system roles (`SUPER_ADMIN`, `OWNER`) are active below.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mt-4">
                    {customRoles
                      .filter((r) => !r.isSystem)
                      .map((cr) => (
                        <div key={cr.id} className="p-3.5 bg-slate-950/70 border border-slate-800 rounded-xl flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-xs text-white">{cr.name}</span>
                              <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-brand-950 text-brand-300 border border-brand-800">
                                {cr.code}
                              </span>
                            </div>
                            <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-1">{cr.description || 'Custom organization role'}</p>
                            <span className="text-[10px] text-emerald-400 font-mono mt-1 block">
                              {cr.grantedPermissionsCount || 0} permissions granted
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => {
                                setEditingRole(cr);
                                setRoleFormData({ code: cr.code, name: cr.name, description: cr.description || '', permissions: [] });
                                setShowEditRoleModal(true);
                              }}
                              className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition"
                              title="Edit Role"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDeleteRole(cr.id, cr.name)}
                              className="p-1.5 text-slate-400 hover:text-rose-400 hover:bg-rose-950/40 rounded-lg transition"
                              title="Delete Role"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* Built-in Platform System Roles Reference */}
              <div className="bg-slate-900/40 border border-slate-800/90 rounded-2xl p-5 shadow-xl space-y-4 backdrop-blur-xl">
                <div className="border-b border-slate-800 pb-3">
                  <h3 className="text-sm font-bold text-white flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-emerald-400" />
                    <span>Default Platform System Roles</span>
                  </h3>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Standard pre-configured roles available across all workspaces. Use the Full Screen Permission Matrix tab to adjust capabilities across these roles.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
                  {ROLES.map((role) => {
                    const meta = ROLE_METADATA[role];
                    const theme = ROLE_THEMES[role];
                    const grantedCount = screens.reduce((acc, s) => {
                      return acc + s.permissions.filter((p) => Boolean(p.roles[role])).length;
                    }, 0);
                    const totalCount = rolePerms.filter((rp) => rp.role === role).length || 32;

                    return (
                      <div
                        key={role}
                        className="p-4 bg-slate-950/70 border border-slate-800/90 rounded-2xl flex flex-col justify-between hover:border-slate-700 transition shadow-md"
                      >
                        <div>
                          <div className="flex items-center justify-between">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-bold border ${theme.badge}`}>
                              {role}
                            </span>
                            <span className="text-[11px] font-mono text-slate-400">
                              {grantedCount} / {totalCount} perms
                            </span>
                          </div>

                          <h4 className="text-xs font-bold text-white mt-2.5">{meta.title}</h4>
                          <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">{meta.description}</p>
                        </div>

                        <div className="mt-3 pt-3 border-t border-slate-800/60 flex items-center justify-between text-[11px]">
                          <span className="text-slate-500">Authority Scope</span>
                          <span className="text-slate-300 font-medium">{meta.level || 'Standard Platform'}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Invite Member Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-brand-400" />
                Invite Team Member
              </h3>
              <button onClick={() => setShowInviteModal(false)} className="text-slate-400 hover:text-white">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">User Email Address</label>
                <input
                  type="email"
                  required
                  placeholder="colleague@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-sm text-white focus:outline-none focus:border-brand-500 placeholder-slate-600"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">Assign Role</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-sm text-white focus:outline-none focus:border-brand-500 cursor-pointer"
                >
                  <option value="MEMBER">MEMBER — Standard workspace member</option>
                  {customRoles
                    .filter((cr) => !cr.isSystem && cr.code !== 'OWNER' && cr.code !== 'SUPER_ADMIN')
                    .map((cr) => (
                      <option key={cr.code} value={cr.code}>
                        {cr.code} — {cr.name}
                      </option>
                    ))}
                </select>
              </div>

              <div className="p-3 bg-slate-950/60 rounded-xl border border-slate-800 text-xs text-slate-400">
                The user will be granted permissions matching the screen matrix configured for the selected role.
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="px-4 py-2 rounded-xl text-xs font-medium bg-slate-800 text-slate-300 hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviting}
                  className="px-5 py-2 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white flex items-center gap-2"
                >
                  {inviting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Send Invitation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal 2: Create Employee & Screen Permissions */}
      {showCreateEmployeeModal && (
        <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl my-6 p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <div>
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-brand-400" />
                  Create Employee & Map Screen Permissions
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  The employee will be created directly under <span className="text-brand-300 font-semibold">{orgName}</span> and mapped to the screens selected below.
                </p>
              </div>
              <button onClick={() => setShowCreateEmployeeModal(false)} className="text-slate-400 hover:text-white">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateEmployee} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">First Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. John"
                    value={employeeFormData.firstName}
                    onChange={(e) => setEmployeeFormData({ ...employeeFormData, firstName: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Last Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Doe"
                    value={employeeFormData.lastName}
                    onChange={(e) => setEmployeeFormData({ ...employeeFormData, lastName: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Email Address *</label>
                  <input
                    type="email"
                    required
                    placeholder="employee@domain.com"
                    value={employeeFormData.email}
                    onChange={(e) => setEmployeeFormData({ ...employeeFormData, email: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500 font-mono"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-semibold text-slate-300">Initial Password *</label>
                    <button
                      type="button"
                      onClick={() => {
                        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$';
                        let p = '';
                        for (let i = 0; i < 12; i++) p += chars.charAt(Math.floor(Math.random() * chars.length));
                        setEmployeeFormData((prev) => ({ ...prev, password: p }));
                      }}
                      className="text-[10px] text-brand-400 hover:text-brand-300 font-semibold"
                    >
                      Generate Strong
                    </button>
                  </div>
                  <input
                    type="text"
                    required
                    placeholder="Min 6 chars"
                    value={employeeFormData.password}
                    onChange={(e) => setEmployeeFormData({ ...employeeFormData, password: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500 font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Initial Role</label>
                <select
                  value={employeeFormData.role}
                  onChange={(e) => setEmployeeFormData({ ...employeeFormData, role: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500"
                >
                  <option value="MEMBER">MEMBER — Standard Employee</option>
                  {customRoles
                    .filter((cr) => !cr.isSystem && cr.code !== 'OWNER' && cr.code !== 'SUPER_ADMIN')
                    .map((cr) => (
                      <option key={cr.code} value={cr.code}>
                        {cr.code} — {cr.name}
                      </option>
                    ))}
                </select>
              </div>

              {/* Screen Visibility Checklist */}
              <div className="pt-2 border-t border-slate-800 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-xs font-bold text-white flex items-center gap-1.5">
                      <Lock className="w-3.5 h-3.5 text-brand-400" />
                      Screen Visibility Controls (Show / Hide per Screen)
                    </h4>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      Check the screens this employee can see. Unchecked screens are completely hidden from their session.
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        const mailOnly: Record<string, boolean> = {};
                        screens.forEach((s) => (mailOnly[s.route] = s.route === '/webmail'));
                        setEmployeeScreenToggles(mailOnly);
                      }}
                      className="px-2 py-1 rounded bg-indigo-950/80 border border-indigo-800/80 hover:bg-indigo-900/80 text-[10px] text-indigo-300 font-semibold flex items-center gap-1"
                    >
                      <Mail className="w-3 h-3" />
                      <span>📧 Mail-Only</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const all: Record<string, boolean> = {};
                        screens.forEach((s) => (all[s.route] = true));
                        setEmployeeScreenToggles(all);
                      }}
                      className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-[10px] text-slate-200 font-medium"
                    >
                      Show All
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEmployeeScreenToggles({
                          '/': true,
                          '/webmail': true,
                          '/campaigns': true,
                          '/templates': true,
                          '/contacts': true,
                          '/deliverability': true,
                          '/team': false,
                          '/billing': false,
                          '/api-keys': false,
                          '/uam': false,
                          '/health': false,
                          '/admin': false,
                        });
                      }}
                      className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-[10px] text-brand-300 font-medium"
                    >
                      Marketing
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto p-1 bg-slate-950/60 rounded-xl border border-slate-800/80">
                  {screens.map((screen) => {
                    const Icon = SCREEN_ICONS[screen.code] || Layers;
                    const isChecked = Boolean(employeeScreenToggles[screen.route]);

                    return (
                      <div
                        key={screen.id}
                        onClick={() =>
                          setEmployeeScreenToggles((prev) => ({
                            ...prev,
                            [screen.route]: !prev[screen.route],
                          }))
                        }
                        className={`p-2.5 rounded-xl border flex items-center justify-between cursor-pointer transition select-none ${
                          isChecked
                            ? 'bg-brand-950/30 border-brand-800/60 text-white'
                            : 'bg-slate-900/40 border-slate-800/60 text-slate-500 hover:bg-slate-900'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${isChecked ? 'text-brand-400' : 'text-slate-600'}`} />
                          <div>
                            <div className="text-xs font-semibold">{screen.name}</div>
                            <div className="text-[10px] font-mono text-slate-500">{screen.route}</div>
                          </div>
                        </div>
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                            isChecked ? 'bg-emerald-950 text-emerald-400 border border-emerald-800' : 'bg-slate-800 text-slate-400'
                          }`}
                        >
                          {isChecked ? 'SHOW' : 'HIDE'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateEmployeeModal(false)}
                  className="px-4 py-2 rounded-xl text-xs font-medium bg-slate-800 text-slate-300 hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingEmployee}
                  className="px-5 py-2 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white flex items-center gap-2 shadow-lg shadow-brand-950/40"
                >
                  {creatingEmployee && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Create & Map Employee
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal 3: Edit Member Screen Permissions */}
      {editingMember && (
        <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-xl p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <div>
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-brand-400" />
                  Screen Permissions: {editingMember.user?.firstName} {editingMember.user?.lastName}
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  Screens marked HIDE are completely blocked from this employee's session.
                </p>
              </div>
              <button onClick={() => setEditingMember(null)} className="text-slate-400 hover:text-white">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            {loadingMemberPerms ? (
              <div className="p-12 text-center text-slate-400 flex flex-col items-center justify-center gap-2">
                <Loader2 className="w-6 h-6 animate-spin text-brand-400" />
                <span className="text-xs">Loading employee screen permissions...</span>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2 pb-1 border-b border-slate-800">
                  <div className="text-xs text-slate-400">
                    Visible: <strong className="text-white">{Object.values(memberScreenToggles).filter(Boolean).length}</strong> / {screens.length} screens
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        const mailOnly: Record<string, boolean> = {};
                        screens.forEach((s) => (mailOnly[s.route] = s.route === '/webmail'));
                        setMemberScreenToggles(mailOnly);
                      }}
                      className="px-2 py-1 rounded bg-indigo-950/80 border border-indigo-800/80 hover:bg-indigo-900/80 text-[10px] text-indigo-300 font-semibold"
                    >
                      📧 Mail-Only
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setMemberScreenToggles({
                          '/': true,
                          '/webmail': true,
                          '/campaigns': true,
                          '/templates': true,
                          '/contacts': true,
                          '/deliverability': true,
                          '/team': false,
                          '/billing': false,
                          '/api-keys': false,
                          '/uam': false,
                          '/health': false,
                          '/admin': false,
                        });
                      }}
                      className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-[10px] text-brand-300 font-medium"
                    >
                      Marketing
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const all: Record<string, boolean> = {};
                        screens.forEach((s) => (all[s.route] = true));
                        setMemberScreenToggles(all);
                      }}
                      className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-[10px] text-slate-200 font-medium"
                    >
                      Show All
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const none: Record<string, boolean> = {};
                        screens.forEach((s) => (none[s.route] = false));
                        setMemberScreenToggles(none);
                      }}
                      className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-[10px] text-slate-400 font-medium"
                    >
                      Hide All
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto p-1 bg-slate-950/60 rounded-xl border border-slate-800/80">
                  {screens.map((screen) => {
                    const Icon = SCREEN_ICONS[screen.code] || Layers;
                    const isChecked = Boolean(memberScreenToggles[screen.route]);

                    return (
                      <div
                        key={screen.id}
                        onClick={() =>
                          setMemberScreenToggles((prev) => ({
                            ...prev,
                            [screen.route]: !prev[screen.route],
                          }))
                        }
                        className={`p-2.5 rounded-xl border flex items-center justify-between cursor-pointer transition select-none ${
                          isChecked
                            ? 'bg-brand-950/30 border-brand-800/60 text-white'
                            : 'bg-slate-900/40 border-slate-800/60 text-slate-500 hover:bg-slate-900'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${isChecked ? 'text-brand-400' : 'text-slate-600'}`} />
                          <div>
                            <div className="text-xs font-semibold">{screen.name}</div>
                            <div className="text-[10px] font-mono text-slate-500">{screen.route}</div>
                          </div>
                        </div>

                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                            isChecked ? 'bg-emerald-950 text-emerald-400 border border-emerald-800' : 'bg-slate-800 text-slate-400'
                          }`}
                        >
                          {isChecked ? 'SHOW' : 'HIDE'}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-800">
                  <button
                    type="button"
                    onClick={() => setEditingMember(null)}
                    className="px-4 py-2 rounded-xl text-xs font-medium bg-slate-800 text-slate-300 hover:bg-slate-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveMemberPermissions}
                    disabled={savingMemberPerms}
                    className="px-5 py-2 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white flex items-center gap-2 shadow-lg shadow-brand-950/40"
                  >
                    {savingMemberPerms && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    Save Screen Permissions
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal 4: Create Custom Role */}
      {showCreateRoleModal && (
        <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-xl p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-brand-400" />
                Create Workspace Custom Role
              </h3>
              <button onClick={() => setShowCreateRoleModal(false)} className="text-slate-400 hover:text-white">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateRole} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Role Identifier (Code) *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. MARKETING_LEAD"
                    value={roleFormData.code}
                    onChange={(e) => setRoleFormData({ ...roleFormData, code: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500 font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Display Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Marketing Lead"
                    value={roleFormData.name}
                    onChange={(e) => setRoleFormData({ ...roleFormData, name: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Role Description</label>
                <textarea
                  rows={2}
                  placeholder="Describe operational responsibilities and access scope..."
                  value={roleFormData.description}
                  onChange={(e) => setRoleFormData({ ...roleFormData, description: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-2">Grant Initial Permissions</label>
                <div className="max-h-52 overflow-y-auto p-2 bg-slate-950/80 rounded-xl border border-slate-800 space-y-2">
                  {screens.map((screen) => (
                    <div key={screen.id} className="p-2 border border-slate-800/80 rounded-lg">
                      <div className="text-[11px] font-bold text-brand-300 mb-1">{screen.name}</div>
                      <div className="grid grid-cols-2 gap-1.5">
                        {screen.permissions.map((p) => {
                          const isChecked = roleFormData.permissions.includes(p.id);
                          return (
                            <label
                              key={p.id}
                              className={`flex items-center gap-2 p-1.5 rounded cursor-pointer text-xs select-none ${
                                isChecked ? 'bg-brand-950/60 text-white' : 'text-slate-400 hover:text-slate-200'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => {
                                  setRoleFormData((prev) => ({
                                    ...prev,
                                    permissions: isChecked
                                      ? prev.permissions.filter((id) => id !== p.id)
                                      : [...prev.permissions, p.id],
                                  }));
                                }}
                                className="rounded border-slate-700 text-brand-600 focus:ring-0"
                              />
                              <span className="truncate">{p.actionName}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowCreateRoleModal(false)}
                  className="px-4 py-2 rounded-xl text-xs font-medium bg-slate-800 text-slate-300 hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={roleActionLoading}
                  className="px-5 py-2 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white flex items-center gap-2"
                >
                  {roleActionLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Create Custom Role
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal 5: Edit Custom Role */}
      {showEditRoleModal && editingRole && (
        <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-xl p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <Edit3 className="w-5 h-5 text-brand-400" />
                Edit Custom Role: {editingRole.code}
              </h3>
              <button onClick={() => setShowEditRoleModal(false)} className="text-slate-400 hover:text-white">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleUpdateRole} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Display Name *</label>
                <input
                  type="text"
                  required
                  value={roleFormData.name}
                  onChange={(e) => setRoleFormData({ ...roleFormData, name: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Role Description</label>
                <textarea
                  rows={2}
                  value={roleFormData.description}
                  onChange={(e) => setRoleFormData({ ...roleFormData, description: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowEditRoleModal(false)}
                  className="px-4 py-2 rounded-xl text-xs font-medium bg-slate-800 text-slate-300 hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={roleActionLoading}
                  className="px-5 py-2 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white flex items-center gap-2"
                >
                  {roleActionLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Save Role Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
